import React, { useState, useEffect, useMemo } from 'react';
import { Card, Input, Select, Spinner, Badge } from '../../components/ds';
import { CATEGORY_ORDER, CATEGORY_CONFIG } from '../../config/categoryConfig';
import { categoryOverrides } from '../../config/categoryOverrides';
import './CategoryViewer.css';

const META_LIST_URL =
  'https://man.vm5apis.com/dist/pmp_v3/development/VM5Series/meta_list.js';

function getOriginalCategory(propertyKey, metaDef) {
  const raw = (metaDef.category || '').toLowerCase();
  if (raw === 'medias') return 'Medias';
  if (raw === 'urls') return 'URLs';
  if (raw === 'trackings') return 'Trackings';
  if (raw === 'advance') return 'Advance';

  // fallback inference
  const type = (metaDef.type || '').toLowerCase();
  if (type === 'image' || type === 'video' || type === 'medias') return 'Medias';
  if (type === 'form' || propertyKey.includes('track') || propertyKey.includes('pixel')) return 'Trackings';
  if (propertyKey.includes('url') || propertyKey.includes('link')) return 'URLs';
  return 'Advance';
}

function getFinalCategory(componentName, propertyKey, metaDef) {
  const override = categoryOverrides[componentName]?.[propertyKey];
  if (override) return override;
  return getOriginalCategory(propertyKey, metaDef);
}

async function loadMetaList() {
  const res = await fetch(META_LIST_URL);
  let text = await res.text();
  text = text.replace(/^export\s+const\s+meta_list\s*=\s*/, '');
  text = text.replace(/;\s*$/, '');
  // eslint-disable-next-line no-new-func
  return new Function(`return (${text})`)();
}

export default function CategoryViewer() {
  const [metaList, setMetaList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterComponent, setFilterComponent] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('component');
  const [sortAsc, setSortAsc] = useState(true);

  useEffect(() => {
    loadMetaList()
      .then(setMetaList)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Build flat rows from meta_list
  const rows = useMemo(() => {
    if (!metaList) return [];
    const result = [];
    for (const [compName, props] of Object.entries(metaList)) {
      for (const [propKey, propDef] of Object.entries(props)) {
        const original = getOriginalCategory(propKey, propDef);
        const final_ = getFinalCategory(compName, propKey, propDef);
        const hasOverride = original !== final_;
        result.push({
          component: compName,
          property: propKey,
          label: propDef.label || '',
          type: propDef.type || '',
          original,
          final: final_,
          hasOverride,
        });

        // Also extract medias/form column sub-fields
        if (propDef.column && Array.isArray(propDef.column)) {
          for (const colGroup of propDef.column) {
            for (const [colKey, colDef] of Object.entries(colGroup)) {
              const colOriginal = getOriginalCategory(colKey, colDef);
              const colFinal = getFinalCategory(compName, colKey, colDef);
              result.push({
                component: compName,
                property: `${propKey}.${colKey}`,
                label: colDef.label || '',
                type: colDef.type || '',
                original: colOriginal,
                final: colFinal,
                hasOverride: colOriginal !== colFinal,
                isSubField: true,
              });
            }
          }
        }
      }
    }
    return result;
  }, [metaList]);

  // Component names for filter
  const componentNames = useMemo(
    () => [...new Set(rows.map((r) => r.component))].sort(),
    [rows]
  );

  // Filtered + sorted rows
  const filteredRows = useMemo(() => {
    let out = rows;
    if (filterComponent) out = out.filter((r) => r.component === filterComponent);
    if (filterCategory) out = out.filter((r) => r.final === filterCategory);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(
        (r) =>
          r.property.toLowerCase().includes(q) ||
          r.label.toLowerCase().includes(q) ||
          r.component.toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      const va = a[sortKey] || '';
      const vb = b[sortKey] || '';
      return sortAsc ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return out;
  }, [rows, filterComponent, filterCategory, search, sortKey, sortAsc]);

  // Stats
  const stats = useMemo(() => {
    const counts = {};
    CATEGORY_ORDER.forEach((c) => (counts[c] = 0));
    rows.forEach((r) => {
      counts[r.final] = (counts[r.final] || 0) + 1;
    });
    const overrideCount = rows.filter((r) => r.hasOverride).length;
    return { counts, overrideCount };
  }, [rows]);

  function handleSort(key) {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  }

  function sortIndicator(key) {
    if (sortKey !== key) return '';
    return sortAsc ? ' ▲' : ' ▼';
  }

  if (loading) return <div className="cv-center"><Spinner /></div>;
  if (error) return <div className="cv-center cv-error">載入失敗：{error}</div>;

  return (
    <div className="cv-page">
      <div className="cv-header">
        <h1>Category Map Viewer</h1>
        <p className="cv-subtitle">檢視 meta_list 所有欄位的分類對照（共 {rows.length} 筆）</p>
      </div>

      <Card className="cv-filters">
        <div className="cv-filter-row">
          <Select
            value={filterComponent}
            onChange={(e) => setFilterComponent(e.target.value)}
          >
            <option value="">All Components ({componentNames.length})</option>
            {componentNames.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </Select>
          <Select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {CATEGORY_ORDER.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_CONFIG[c].label} ({c})
              </option>
            ))}
          </Select>
          <Input
            placeholder="搜尋欄位名稱..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </Card>

      <Card className="cv-stats">
        <div className="cv-stat-row">
          {CATEGORY_ORDER.map((c) => (
            <span key={c} className="cv-stat-item">
              {CATEGORY_CONFIG[c].icon} <span>{c}: <strong>{stats.counts[c]}</strong></span>
            </span>
          ))}
          <span className="cv-stat-item cv-stat-override">
            Override: <strong>{stats.overrideCount}</strong>
          </span>
        </div>
      </Card>

      <Card className="cv-table-wrap">
        <table className="cv-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('component')}>
                Component{sortIndicator('component')}
              </th>
              <th onClick={() => handleSort('property')}>
                Property{sortIndicator('property')}
              </th>
              <th onClick={() => handleSort('label')}>
                Label{sortIndicator('label')}
              </th>
              <th onClick={() => handleSort('type')}>
                Type{sortIndicator('type')}
              </th>
              <th onClick={() => handleSort('original')}>
                Original{sortIndicator('original')}
              </th>
              <th onClick={() => handleSort('final')}>
                Final{sortIndicator('final')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r, i) => (
              <tr
                key={`${r.component}-${r.property}-${i}`}
                className={r.hasOverride ? 'cv-row-override' : ''}
              >
                <td>{r.component}</td>
                <td>
                  {r.isSubField && <span className="cv-sub-indent">└ </span>}
                  {r.property}
                </td>
                <td className="cv-label-cell">{r.label}</td>
                <td><Badge variant="neutral">{r.type || '-'}</Badge></td>
                <td>
                  <Badge variant="neutral">{r.original}</Badge>
                </td>
                <td>
                  <Badge variant={r.hasOverride ? 'warning' : 'neutral'}>
                    {r.final}
                  </Badge>
                  {r.hasOverride && ' ⚡'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredRows.length === 0 && (
          <div className="cv-empty">沒有符合條件的欄位</div>
        )}
      </Card>
    </div>
  );
}
